/**
 * The browser's copy of the SQLite file.
 *
 * The phone's database is a file that survives a restart. The browser's lives
 * in memory, so it is written to IndexedDB as one blob after it changes and
 * read back on the next boot. It holds what only this device knows (the pinned
 * insights, the Dashboard layout, the active business, the setup progress) as
 * well as the mirror, which the next pull would refill anyway.
 *
 * Every failure here is quiet by design. A private window, a blocked store or
 * a full disk leaves a working app that starts from an empty mirror; it must
 * never leave a broken one.
 */
const DATABASE = 'alluzo-web';
const STORE = 'sqlite';
const KEY = 'mirror';

const open = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB did not open.'));
  });

const run = async <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed.'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted.'));
    });
  } finally {
    db.close();
  }
};

export const loadSnapshot = async (): Promise<Uint8Array | null> => {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const value: unknown = await run('readonly', (store) => store.get(KEY));
    return value instanceof Uint8Array ? value : null;
  } catch {
    return null;
  }
};

/** Whether the copy was written. Never throws: see the header. */
export const saveSnapshot = async (bytes: Uint8Array): Promise<boolean> => {
  if (typeof indexedDB === 'undefined') return false;
  try {
    await run('readwrite', (store) => store.put(bytes, KEY));
    return true;
  } catch {
    /* See the header: a snapshot that cannot be written costs a refill, nothing more. */
    return false;
  }
};

export const clearSnapshot = async (): Promise<void> => {
  if (typeof indexedDB === 'undefined') return;
  try {
    await run('readwrite', (store) => store.delete(KEY));
  } catch {
    /* Quiet, as above. */
  }
};
